"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { ChefHat, UtensilsCrossed, Users, Calendar, Clock, Star, CheckCircle2, Phone, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { SEO } from "@/components/seo"
import { useGetPublicSettingsQuery } from "@/store"
import { CateringRequestForm } from "@/components/CateringRequestForm"
import { PrivateChefRequestForm } from "@/components/PrivateChefRequestForm"

const services = [
  {
    icon: ChefHat,
    title: "Private Chef Services",
    description: "Experience the luxury of having a professional chef prepare exquisite Nigerian delicacies in the comfort of your own home. Perfect for intimate dinners, special occasions, or when you want restaurant-quality meals without leaving home.",
    features: [
      "Customized menu planning based on your preferences",
      "Fresh ingredients sourced and prepared on-site",
      "Professional presentation and service",
      "Flexible scheduling to fit your needs",
      "Dietary restrictions and allergies accommodated",
      "Clean-up service included"
    ],
    pricing: "Starting from ₦50,000 per event",
    color: "from-orange-500 to-amber-500",
    bgColor: "bg-orange-500/10",
    iconColor: "text-orange-500"
  },
  {
    icon: UtensilsCrossed,
    title: "Catering Services",
    description: "Make your events memorable with our professional catering services. From corporate events to weddings, birthdays to anniversaries, we deliver exceptional Nigerian cuisine that will impress your guests.",
    features: [
      "Event planning and consultation",
      "Wide variety of Nigerian delicacies",
      "Buffet and plated service options",
      "Professional serving staff available",
      "Equipment rental options",
      "Suitable for events of all sizes"
    ],
    pricing: "Custom quotes based on event size",
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-500/10",
    iconColor: "text-amber-500"
  }
]

const benefits = [
  {
    icon: Star,
    title: "Premium Quality",
    description: "Only the finest ingredients and traditional recipes"
  },
  {
    icon: Users,
    title: "Experienced Team",
    description: "Professional chefs with years of culinary expertise"
  },
  {
    icon: Calendar,
    title: "Flexible Booking",
    description: "Available for events and occasions throughout the year"
  },
  {
    icon: Clock,
    title: "Timely Service",
    description: "Punctual delivery and service that respects your schedule"
  }
]

export default function ServicesScreen() {
  const { data: publicSettings } = useGetPublicSettingsQuery();
  const businessInfo = publicSettings?.data || {};
  
  const defaultPhone = "09017104173";
  const defaultEmail = "zestandzeallunches@gmail.com";

  const [isCateringFormOpen, setIsCateringFormOpen] = useState(false)
  const [isPrivateChefFormOpen, setIsPrivateChefFormOpen] = useState(false)

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <SEO
        title="Catering & Private Chef Services in Lagos."
        description="Elevate your events with our premium catering and private chef services in Lagos. Professional Nigerian cuisine for weddings, corporate events, and intimate dinners in Lagos"
        canonical="/services"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Services", url: "/services" },
        ]}
      />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Elevate your dining experience with our premium private chef and catering services. 
          We bring authentic Nigerian delicacies to your table, prepared with passion and expertise.
        </p>
      </motion.div>

      {/* Services Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
        {services.map((service, index) => {
          const Icon = service.icon
          return (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
            >
              <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-2">
                <CardHeader>
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`rounded-lg bg-gradient-to-r ${service.color === "from-orange-500 to-amber-500" ? "from-orange-500/10 to-amber-500/10" : "from-amber-500/10 to-yellow-500/10"} p-4`}>
                      <Icon className={`h-8 w-8 ${service.color === "from-orange-500 to-amber-500" ? "text-orange-500" : "text-amber-500"}`} />
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-2">{service.title}</CardTitle>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-orange-500" />
                        What's Included:
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent mt-1">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-foreground mb-2">Pricing:</p>
                      <p className="text-lg font-bold bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                        {service.pricing}
                      </p>
                    </div> */}
                    <Button 
                      className={`w-full mt-4 bg-gradient-to-r ${service.color} hover:opacity-90 text-white`}
                      size="lg"
                      onClick={() => {
                        if (service.title === "Catering Services") {
                          setIsCateringFormOpen(true)
                        } else if (service.title === "Private Chef Services") {
                          setIsPrivateChefFormOpen(true)
                        }
                      }}
                    >
                      <Phone className="mr-2 h-4 w-4" />
                      Request a Quote
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>

      {/* Benefits Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mb-16"
      >
        <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-full text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="mb-4 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-4">
                      <Icon className="h-6 w-6 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            )
          })}
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-2">
          <CardContent className="pt-8 pb-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-muted-foreground mb-6">
                Contact us today to discuss your event or private dining needs. 
                We'll work with you to create an unforgettable culinary experience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white" asChild>
                  <a href={`tel:${businessInfo.phone || defaultPhone}`}>
                    <Phone className="mr-2 h-4 w-4" />
                    Call Us: {businessInfo.phone || defaultPhone}
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href={`mailto:${businessInfo.email || defaultEmail}`}>
                    <Mail className="mr-2 h-4 w-4" />
                    Send an Email
                  </a>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Catering Request Form Modal */}
      <CateringRequestForm 
        open={isCateringFormOpen} 
        onOpenChange={setIsCateringFormOpen} 
      />

      {/* Private Chef Request Form Modal */}
      <PrivateChefRequestForm 
        open={isPrivateChefFormOpen} 
        onOpenChange={setIsPrivateChefFormOpen} 
      />
    </div>
  )
}

